import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import type { AppDispatch, RootState } from "../../app/store";
import { loadBackgroundProcessSummaryForObject } from "./backgroundProcessingApi";
import { isFinalStatus } from "./backgroundProcessStatus";
import {
  clearPersistent,
  markPersistent,
  processCompleted,
  selectPollableProcesses,
  subscribeProcess,
  unsubscribeProcess,
  upsertProcess,
} from "./backgroundProcessingSlice";
import { crossTabSupported, runAsLeader } from "./crossTab";

/**
 * backgroundPollerMiddleware — the single owner of background-process polling,
 * coordinated ACROSS TABS.
 *
 * - Polling runs in exactly ONE tab: the Web Locks leader (see crossTab). Other
 *   tabs do not hit the network.
 * - The leader fans its results out over a BroadcastChannel; every tab applies
 *   them to its own store, so all tabs show the same status without polling.
 * - When the leader tab closes, a follower acquires the lock and resumes.
 *
 * If Web Locks / BroadcastChannel are unavailable, this degrades to per-tab
 * polling (every tab is its own leader, no fan-out) — the previous behaviour.
 *
 * TODO (optimisation): replace per-key polls with one batched
 * `loadMyBackgroundProcessesSummary` request (N→1).
 */

const POLL_MS = 10000;
const CHANNEL_NAME = "background-processing";
// Only state-sync actions are mirrored to other tabs. `processCompleted` is kept
// leader-local so the completion side effect (toast) fires once, in the leader.
const SYNC_TYPES = new Set<string>([upsertProcess.type, markPersistent.type, clearPersistent.type]);

export const backgroundPollerMiddleware = createListenerMiddleware();

let storeApi: { dispatch: AppDispatch; getState: () => RootState } | null = null;
let timer: ReturnType<typeof setInterval> | null = null;
let isLeader = false;
let initialised = false;
let channel: BroadcastChannel | null = null;
// key -> processNumber (or "final") already announced, so processCompleted fires once per run.
const emittedCompletion = new Map<string, number | "final">();

const stop = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }
};

const tick = async () => {
  if (!storeApi) return;
  const { dispatch, getState } = storeApi;

  const pollable = selectPollableProcesses(getState());
  if (pollable.length === 0) {
    stop();
    return;
  }

  await Promise.all(
    pollable.map(async (p) => {
      const result = await dispatch(
        loadBackgroundProcessSummaryForObject({ objectType: p.objectType, objectId: p.objectId }),
      );
      if (!loadBackgroundProcessSummaryForObject.fulfilled.match(result)) return;

      const dto = result.payload;
      const patch = {
        objectType: p.objectType,
        objectId: p.objectId,
        status: dto.status,
        operationType: dto.operationType,
        processNumber: dto.processNumber,
      };
      dispatch(upsertProcess(patch)); // mirrored to other tabs by the effect below

      if (isFinalStatus(dto.status)) {
        const key = `${p.objectType}:${p.objectId}`;
        const token = dto.processNumber ?? "final";
        if (emittedCompletion.get(key) !== token) {
          emittedCompletion.set(key, token);
          dispatch(processCompleted(patch)); // leader-local: drives the completion side effect
        }
      }
    }),
  );

  if (selectPollableProcesses(getState()).length === 0) stop();
};

const reconcile = () => {
  if (!storeApi) return;
  const hasWork = selectPollableProcesses(storeApi.getState()).length > 0;
  if (isLeader && hasWork && !timer) {
    void tick(); // immediate, then on the interval
    timer = setInterval(() => void tick(), POLL_MS);
  } else if ((!isLeader || !hasWork) && timer) {
    stop();
  }
};

const initialise = (dispatch: AppDispatch, getState: () => RootState) => {
  if (initialised) return;
  initialised = true;
  storeApi = { dispatch, getState };

  if (!crossTabSupported()) {
    isLeader = true; // per-tab fallback: poll independently, no fan-out
    return;
  }

  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent) => {
    const action = event.data as { type?: string; payload?: unknown; meta?: Record<string, unknown> };
    if (!action?.type || !storeApi) return;
    // Re-dispatch locally, flagged so the effect doesn't echo it back.
    storeApi.dispatch({ ...action, meta: { ...(action.meta ?? {}), fromBroadcast: true } } as never);
  };

  // Elect this tab; only the leader runs the polling timer.
  runAsLeader("background-poller", () => {
    isLeader = true;
    reconcile();
  });
};

backgroundPollerMiddleware.startListening({
  matcher: isAnyOf(
    subscribeProcess,
    unsubscribeProcess,
    upsertProcess,
    processCompleted,
    markPersistent,
    clearPersistent,
  ),
  effect: (action, api) => {
    initialise(api.dispatch as AppDispatch, api.getState as () => RootState);

    // Fan state-sync actions out to other tabs (skip ones we received ourselves).
    const meta = (action as { meta?: { fromBroadcast?: boolean } }).meta;
    if (channel && SYNC_TYPES.has(action.type) && !meta?.fromBroadcast) {
      channel.postMessage({
        type: action.type,
        payload: (action as { payload?: unknown }).payload,
      });
    }

    reconcile();
  },
});
